// OIDC Authorization Code + PKCE flow for Keycloak

import { brand, BRAND_DEFAULTS, brandLocales, brandDefaultLocale } from '@parsina/framework/ui/lib/brand';
import { KC_BASE_URL, kcRealm, kcClientId } from './runtimeEnv.js';
import { clearPhiSearchTerms } from './phiSearchStore.js';

// The realm's URL, resolved on every call like the client id (kcClientId()):
// both come from the deployment, and runtimeEnv.js says in which order.
const kcBase = () => `${KC_BASE_URL}/realms/${encodeURIComponent(kcRealm())}`;

// Always derived at call time so redirect_uri is consistent across
// the auth request and token exchange even if the page reloads.
function getRedirectUri() {
  return `${window.location.origin}/`;
}

// ── Login language ───────────────────────────────────────────────────────────
// The sign-in screen — and, more importantly, every error Keycloak resolves
// server-side before the theme ever runs — follows the language the panel is
// in. Without this the realm picks for itself, so a doctor working in Persian
// could be sent to an English login page and shown English errors on it.
//
// `<html lang>` is the live panel language: index.html's inline bootstrap sets
// it from the cached choice before this module is even parsed, and I18nProvider
// keeps it current after that. Read here rather than imported from the i18n module so
// this stays a leaf module with no React dependency.
//
// Both parameters are sent on purpose: `ui_locales` is the OIDC standard hint,
// `kc_locale` is the one Keycloak acts on — it also sets the KEYCLOAK_LOCALE
// cookie, which is what carries the language through the rest of the flow
// (password step, OTP step, any required action) rather than just the first page.
// Deliberately NOT the panel's locale list but the sign-in theme's: the
// languages the RELEASED parsina.keycloakify carries message bundles for
// (IMPROVED_LANGUAGE_TAGS — fa, en, ar). Sending `kc_locale` for a language the
// theme lacks gets a login page half in Keycloak's own fallback, and the live
// theme records what Keycloak resolved for an explicit `kc_locale` as the
// browser's stored choice — so a language joins this list only on the day the
// theme that carries it is live. French (PM-0185) and German (PM-0212) are
// built in the theme but not released: add 'fr' / 'de' here with that release
// (FRENCH.md D11/D20; German follows the same plan), and not before. Until then
// a panel language the theme lacks signs in through the language the panel
// itself falls back to (LOGIN_FALLBACK: fr → en, de → en, D4 — never Persian
// for a French or German reader), else through the default below.
//
// A deployment's brand (docs/rebranding/CONTRACT.md) narrows the list to the
// languages it offers and names the fallback: a brand without Persian signs a
// panel in another language through its own default language, never through
// Persian. With no brand this is the list above and Persian, exactly as before.
// A brand of its own also signs in through a theme of its own — Proxia's
// (proxia.keycloakify, realm locales en + fa) carries no Arabic, French or
// German — so it keeps the two languages every theme carries. The brand's
// helpers are dependency-free, so this module stays a leaf.
const LOGIN_LOCALES = ['fa', 'en', 'ar'];
const ANY_THEME_LOCALES = ['fa', 'en'];
const LOGIN_FALLBACK = { fr: 'en', de: 'en' };

/** The `ui_locales`/`kc_locale` every Keycloak redirect carries — exported for tests. */
export function panelLocale() {
  const lang = (document.documentElement.getAttribute('lang') || '')
    .toLowerCase()
    .split('-')[0];
  const theme = brand().id === BRAND_DEFAULTS.id ? LOGIN_LOCALES : ANY_THEME_LOCALES;
  const offered = brandLocales(theme);
  if (offered.includes(lang)) return lang;
  const next = LOGIN_FALLBACK[lang];
  return next && offered.includes(next) ? next : brandDefaultLocale('fa', theme);
}

// ── PKCE helpers ─────────────────────────────────────────────────────────────

function randomBytes(len) {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return arr;
}

function base64url(buf) {
  return btoa(String.fromCharCode(...buf))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function sha256(str) {
  const enc = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return new Uint8Array(buf);
}

async function generateChallenge(verifier) {
  return base64url(await sha256(verifier));
}

// ── Token storage ─────────────────────────────────────────────────────────────

// A sign-in lasts as long as Keycloak's refresh token — 30 days on beta1 — not
// as long as one tab (PM-0158). The REFRESH token and the id token (the logout
// hint) live in localStorage, so a new tab, a reopened browser or a Chrome that
// Android killed in the background signs in silently from them. The 5-minute
// ACCESS token stays per tab. Before this, all three lived in sessionStorage and
// every new tab or browser restart threw a valid 30-day token away and sent the
// user to the login form. A copy left in sessionStorage by an older build is
// still read, so a tab open across the deploy keeps working.
const PERSISTED = ['kc_refresh', 'kc_id_token'];
function durable() {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}
function readPersisted(key) {
  try {
    const v = durable()?.getItem(key);
    if (v) return v;
  } catch { /* storage blocked: fall through to the tab's copy */ }
  return sessionStorage.getItem(key);
}

export function getToken()   { return sessionStorage.getItem('kc_token'); }
export function getRefresh() { return readPersisted('kc_refresh'); }
export function getIdToken() { return readPersisted('kc_id_token'); }

function saveTokens(token, refresh, idToken) {
  sessionStorage.setItem('kc_token', token);
  const pairs = [['kc_refresh', refresh], ['kc_id_token', idToken]].filter(([, v]) => v);
  try {
    const store = durable();
    if (!store) throw new Error('no localStorage');
    pairs.forEach(([k, v]) => store.setItem(k, v));
    pairs.forEach(([k]) => sessionStorage.removeItem(k));
  } catch {
    // Private mode or blocked storage: the tab still works, as it always did.
    pairs.forEach(([k, v]) => sessionStorage.setItem(k, v));
  }
}

export function clearTokens() {
  ['kc_token', 'kc_refresh', 'kc_id_token', 'kc_verifier', 'kc_state'].forEach(k =>
    sessionStorage.removeItem(k)
  );
  try { PERSISTED.forEach(k => durable()?.removeItem(k)); } catch { /* nothing stored */ }
}

// Keycloak ROTATES the refresh token. With it shared across tabs, two tabs
// refreshing at the same moment would present the same token twice and the
// second would be refused — and a refused refresh signs the user out. The
// browser's Web Locks serialise refreshes across every tab of this origin; each
// refresh reads the stored token inside the lock, so it uses the one the
// previous tab just received. Where Web Locks are missing, the per-tab
// single-flight below still holds.
async function withRefreshLock(fn) {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  if (!locks || typeof locks.request !== 'function') return fn();
  return locks.request('parsina-kc-refresh', () => fn());
}

// ── Redirect-loop guard ───────────────────────────────────────────────────────
// Records each redirect timestamp. If 3+ happen within 30 s, stops redirecting
// and lets the app render an error screen instead.

const LOOP_KEY   = 'kc_redirect_ts';
const LOOP_LIMIT = 3;
const LOOP_WIN   = 30_000; // ms

function redirectAllowed() {
  const raw    = sessionStorage.getItem(LOOP_KEY);
  const hist   = raw ? JSON.parse(raw) : [];
  const now    = Date.now();
  const recent = hist.filter(ts => now - ts < LOOP_WIN);
  if (recent.length >= LOOP_LIMIT) return false;
  sessionStorage.setItem(LOOP_KEY, JSON.stringify([...recent, now]));
  return true;
}

export function clearRedirectGuard() {
  sessionStorage.removeItem(LOOP_KEY);
}

// ── Auth flow ─────────────────────────────────────────────────────────────────

export async function redirectToLogin() {
  if (!redirectAllowed()) {
    // Too many consecutive redirects — surface the error to the user instead.
    clearTokens();
    window.history.replaceState({}, '', '/?auth_error=loop');
    window.location.reload();
    return;
  }

  const verifier  = base64url(randomBytes(32));
  const challenge = await generateChallenge(verifier);
  const state     = base64url(randomBytes(16));

  sessionStorage.setItem('kc_verifier', verifier);
  sessionStorage.setItem('kc_state',    state);

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             kcClientId(),
    redirect_uri:          getRedirectUri(),
    scope:                 'openid profile email',
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
    ui_locales:            panelLocale(),
    kc_locale:             panelLocale(),
  });

  window.location.href = `${kcBase()}/protocol/openid-connect/auth?${params}`;
}

export async function handleCallback() {
  const params   = new URLSearchParams(window.location.search);
  const error    = params.get('error');
  const code     = params.get('code');
  const state    = params.get('state');
  const verifier = sessionStorage.getItem('kc_verifier');
  const saved    = sessionStorage.getItem('kc_state');

  // Keycloak returned an explicit error (access_denied, login_required, etc.)
  if (error) {
    clearTokens();
    window.history.replaceState({}, '', '/');
    return false;
  }

  // Missing or mismatched PKCE / state — don't retry immediately
  if (!code || !verifier || state !== saved) {
    window.history.replaceState({}, '', '/');
    return false;
  }

  const res = await fetch(`${kcBase()}/protocol/openid-connect/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'authorization_code',
      client_id:     kcClientId(),
      redirect_uri:  getRedirectUri(),
      code,
      code_verifier: verifier,
    }),
  });

  window.history.replaceState({}, '', '/');

  if (!res.ok) return false;

  const data = await res.json();
  if (!data.access_token) return false;

  saveTokens(data.access_token, data.refresh_token, data.id_token);
  clearRedirectGuard(); // successful auth — reset loop counter
  return true;
}

// In-flight refresh, shared by every caller.
//
// Keycloak rotates the refresh token, so the second concurrent refresh presents
// one the first already spent — Keycloak rejects it, clearTokens() fires, and the
// user is thrown to the login screen mid-task. Anything that fans out hits this:
// two queries in a Promise.all, or the DICOM viewer, which loads a whole series
// in parallel. Everyone waits on the same promise instead.
let refreshInFlight = null;

async function refreshAccessTokenInTab() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

async function doRefresh() {
  const refresh = getRefresh();
  if (!refresh) return false;

  const res = await fetch(`${kcBase()}/protocol/openid-connect/token`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type:    'refresh_token',
      client_id:     kcClientId(),
      refresh_token: refresh,
    }),
  });

  if (!res.ok) { clearTokens(); return false; }
  const data = await res.json();
  if (!data.access_token) { clearTokens(); return false; }
  saveTokens(data.access_token, data.refresh_token, data.id_token);
  return true;
}

// getValidToken returns a usable access token, refreshing first if the current
// one has expired. Lives here rather than in bffClient because the DICOM viewer
// needs it too: Cornerstone fetches image bytes through its own XHR, so without
// this it would be the one caller that never refreshes.
export async function getValidToken() {
  let token = getToken();
  if (!token) return null;
  if (isTokenExpired(token)) {
    if (!await refreshAccessToken()) return null;
    token = getToken();
  }
  return token;
}

export function logout() {
  const idToken = getIdToken();
  clearTokens();
  clearRedirectGuard();
  // Search terms can be patient names; they never outlive the session.
  clearPhiSearchTerms();
  const params = new URLSearchParams({
    client_id:                kcClientId(),
    post_logout_redirect_uri: getRedirectUri(),
    ui_locales:               panelLocale(),
    kc_locale:                panelLocale(),
  });
  if (idToken) params.set('id_token_hint', idToken);
  window.location.href = `${kcBase()}/protocol/openid-connect/logout?${params}`;
}

export function parseJwt(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

export function isTokenExpired(token) {
  const p = parseJwt(token);
  if (!p) return true;
  return Date.now() >= p.exp * 1000 - 30_000; // 30 s buffer
}

// The exported refresh: one per tab (single-flight above), one at a time across
// tabs (Web Lock), so a rotated refresh token is never presented twice (PM-0158).
export function refreshAccessToken() {
  return withRefreshLock(() => refreshAccessTokenInTab());
}
